import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Button,
  Select,
  message,
  Form,
  Input,
  Spin,
} from 'antd';
import DatePicker from '../DatePicker';
import { connect } from 'react-redux';
import { validPhoneNumber } from '../../global/validation';
import ModalWrapper from './ModalWrapper';
import { getRecordResponseById } from '../../actions/replies';
import {
  hasFeatureInLocalStorage,
  REFERRAL_REPLY_DEPARTMENT_FILTER_FEATURE,
} from '../../global/featureFlags';
import { getCachedReferralReplyTemplates } from '../../util/referralTemplateStorage';

class ReferralReply extends Component {
  constructor(props) {
    super(props);
    const patientFirstName = this.props.modalType.data.patientFirstName || '';
    const patientMiddleName = this.props.modalType.data.patientMiddleName || '';
    const patientLastName = this.props.modalType.data.patientLastName || '';

    const patientName = this.props.modalType.data.patientName
    || [patientFirstName, patientMiddleName, patientLastName].filter(Boolean).join(' ');

    this.state = {
      sendToken: this.props.modalType.data.sendToken,
      patientName,
      patientFirstName,
      patientMiddleName,
      patientLastName,
      referringProvider: this.props.modalType.data.referringProvider,
      patientDOB: this.props.modalType.data.patientDOB,
      fileToken: this.props.modalType.data.fileToken,
      ownerType: this.props.modalType.data.ownerType,
      ownerId: this.props.modalType.data.ownerId,
      callerName: this.getTenDigitPhoneNumber(this.props.modalType.data.caller_name),
      templates: getCachedReferralReplyTemplates(),
      isPreviewLoading: false,
      resentRecordSlug: this.props.modalType.data.resentRecordSlug,
      resentReplySlug: this.props.modalType.data.resentReplySlug,
      resendDetailsChanged: false,
      isUsingOriginal: false,
      referralReplyUri: this.props.modalType.data.referralReplyUri,
      appointmentDate: this.props.modalType.data.appointmentDate,
    };
  }

  componentDidMount = async () => {
    const { getReferralDocumentStatus, modalType } = this.props;
    const { sendToken, fileToken } = this.state;

    await getReferralDocumentStatus(fileToken, sendToken, 'received_fax');

    // A pre-selected template (resend flow) may belong to a different department than the
    // record. With the department filter on it is hidden from the dropdown, so drop it and
    // force a fresh pick — otherwise the submit button stays enabled and would silently
    // resend a wrong-department template.
    const presetTemplate = modalType.data.referralTemplate;
    const presetFilteredOut = !!presetTemplate
      && !this.getVisibleTemplates().some(template => template.slug === presetTemplate);

    this.setState({
      selectedTemplate: presetFilteredOut
        ? undefined
        : (presetTemplate || (modalType.data.resentReplySlug && 'original_reply')),
      isUsingOriginal: modalType.data.resentReplySlug && !presetTemplate,
      resendDetailsChanged: presetFilteredOut,
    });
  }

  // When the department filter feature is on and the record belongs to a department,
  // only show that department's templates plus org-level templates. Records without a
  // department (User/Organization-owned) fall back to showing all accessible templates.
  getVisibleTemplates = () => {
    const { templates, ownerType, ownerId } = this.state;
    const departmentFilterEnabled = hasFeatureInLocalStorage(
      REFERRAL_REPLY_DEPARTMENT_FILTER_FEATURE,
    );

    if (departmentFilterEnabled && ownerType === 'Department') {
      return templates.filter(template => (
        template.owner_type === 'Organization'
        || (template.owner_type === 'Department' && String(template.owner_id) === String(ownerId))
      ));
    }

    return templates;
  }

  getTenDigitPhoneNumber = (phoneNumber) => {
    return phoneNumber.replace(/\D/g, '').slice(-11);
  };

  handleTemplateChange = (value) => {
    this.setState({ 
      selectedTemplate: value, 
      resendDetailsChanged: true,
      isUsingOriginal: false,
    });
  }

  handleFaxNumberChange = (e) => {
    this.setState({
      callerName: e.target.value,
      resendDetailsChanged: true,
      isUsingOriginal: false,
    });
  }

  handleDateChange = (_, dateString) => {
    this.setState({
      patientDOB: dateString,
      resendDetailsChanged: true,
      isUsingOriginal: false,
    });
  }

  handleAppointmentDateChange = (_, dateString) => {
    this.setState({
      appointmentDate: dateString === '' ? null : dateString,
      resendDetailsChanged: true,
      isUsingOriginal: false,
    });
  }

  handleNameChange = (e) => {
    this.setState({
      patientName: e.target.value,
      resendDetailsChanged: true,
      isUsingOriginal: false,
    });
  }

  handleFirstNameChange = (e) => {
    this.setState({
      patientFirstName: e.target.value,
      resendDetailsChanged: true,
      isUsingOriginal: false,
    });
  };

  handleMiddleNameChange = (e) => {
    this.setState({
      patientMiddleName: e.target.value,
      resendDetailsChanged: true,
      isUsingOriginal: false,
    });
  }

  handleLastNameChange = (e) => {
    this.setState({
      patientLastName: e.target.value,
      resendDetailsChanged: true,
      isUsingOriginal: false,
    });
  }

  handleProviderChange = (e) => {
    this.setState({
      referringProvider: e.target.value,
      resendDetailsChanged: true,
      isUsingOriginal: false,
    });
  }

  handleDownload = async () => {
    const { getReferralReplyPreviewUrl } = this.props;
    const {
      selectedTemplate,
      sendToken,
      patientDOB,
      patientName,
      referringProvider,
      isUsingOriginal,
      resentRecordSlug,
      appointmentDate,
    } = this.state;

    this.setState({ isPreviewLoading: true });
    const formattedAppointmentDate = appointmentDate && moment(appointmentDate).format('MM/DD/YYYY h:mm A');

    try {
      await getReferralReplyPreviewUrl(
        sendToken,
        patientName,
        patientDOB,
        referringProvider,
        selectedTemplate,
        formattedAppointmentDate,
        isUsingOriginal ? resentRecordSlug : null,
      );
      window.open(this.props.previewUrl.signed_url);
    } catch (error) {
      message.error('Failed to generate preview. Please try again.');
    } finally {
      this.setState({ isPreviewLoading: false });
    }
  }

  handleSend = async () => {
    const {
      hideModal, modalType: { data: { sendToken } },
    } = this.props;
    const { sendReferralReply } = this.props;
    const {
      selectedTemplate,
      callerName,
      patientDOB,
      patientFirstName,
      patientMiddleName,
      patientLastName,
      referringProvider,
      resentReplySlug,
      resendDetailsChanged,
      isUsingOriginal,
      referralReplyUri,
      appointmentDate,
    } = this.state;
    const splitDate = patientDOB.split('/');
    const formattedDOB = `${splitDate[2]}-${splitDate[0].padStart(2, '0')}-${splitDate[1].padStart(2, '0')}`;

    // Convert appointmentDate to UTC before sending
    let utcAppointmentDate = appointmentDate;
    let localAppointmentDate = null;
    if (appointmentDate) {
      const isoMoment = moment(appointmentDate, moment.ISO_8601, true);
      if (!isoMoment.isValid()) { // If we fetch from the DB, it's already in UTC, so this won't run
        utcAppointmentDate = moment(appointmentDate, "MM/DD/YYYY hh:mm A").utc().format("MM/DD/YYYY hh:mm A");
      } else {
        utcAppointmentDate = isoMoment.utc().format("MM/DD/YYYY hh:mm A");
      }
      localAppointmentDate = moment.utc(utcAppointmentDate, "MM/DD/YYYY hh:mm A").local().format("MM/DD/YYYY hh:mm A");
    }
    // Construct full patient name from individual fields
    const fullPatientName = [patientFirstName, patientMiddleName, patientLastName].filter(Boolean).join(' ');

    await sendReferralReply({
      sendToken,
      selectedTemplate: isUsingOriginal ? null : selectedTemplate,
      callerName,
      patientDOB: formattedDOB,
      patientName: fullPatientName,
      patientFirstName,
      patientMiddleName,
      patientLastName,
      referringProvider,
      resentReplySlug,
      resendDetailsChanged,
      appointmentDate: utcAppointmentDate,
      localAppointmentDate,
    });

    const { error, errorCode } = this.props;
    const errorMessageContent = (errorCode === 422) ? 'You must create an address book contact for the received fax phone number before you can reply to the fax.' : 'Error sending reply. Please try again.';
    hideModal();
    await this.props.getRecordResponseById(referralReplyUri);
    if (!error) {
      message.success({
        content: 'Referral Reply will be sent shortly.',
      });
    } else {
      message.error({
        content: errorMessageContent,
      });
    }
  }

  render() {
    const { isLoading, hideModal } = this.props;
    const {
      selectedTemplate,
      templates,
      callerName,
      patientDOB,
      patientFirstName,
      patientMiddleName,
      patientLastName,
      referringProvider,
      resentReplySlug,
      resendDetailsChanged,
      isUsingOriginal,
      appointmentDate,
    } = this.state;

    const visibleTemplates = this.getVisibleTemplates();

    const dateFormat = ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY'];
    const validatePhoneNumber = (rule, value) => {
      if (!value) {
        return Promise.reject('Please enter the fax number!');
      } if (!validPhoneNumber(value)) {
        return Promise.reject('Please enter a valid fax number!');
      }
      return Promise.resolve();
    };

    const isSubmitButtonDisabled = () => {
      if (isUsingOriginal) {
        return false;
      }
      
      const selectionIsValid = !selectedTemplate
        || callerName === null || callerName === ''
        || patientDOB === null || patientDOB === ''
        || patientFirstName === null || patientFirstName === ''
        || patientLastName === null || patientLastName === ''
        || referringProvider === null || referringProvider === '';

      return resentReplySlug ? (resendDetailsChanged && selectionIsValid) : (selectionIsValid || !validPhoneNumber(callerName));
    };

    const customContentStyle = {
      width: '270px',
    };

    return (
      <ModalWrapper
        hideModal={hideModal}
        customContentStyle={customContentStyle}
        action={this.handleSend}
        actionName="Send"
        dismiss={isLoading ? '' : 'Cancel'}
        disabled={isSubmitButtonDisabled()}
        modalTitle="Send Referral Reply"
        form="referralReply"
        width="550px"
        isDraggable
      >
        <div>
          <div style={{ marginBottom: '20px', padding: '10px' }}>
            {!isUsingOriginal && visibleTemplates.length === 0 && (
              <div style={{ color: 'red', marginBottom: '10px' }}>
                {templates.length === 0
                  ? '* Your Organization does not have any referral reply templates. Please contact your Admin.'
                  : '* No referral reply templates are available for this department. Please contact your Admin.'}
              </div>
            )}
            <div style={{ marginBottom: '5px', padding: '10px' }}>
              {/* Template and Fax Number Section */}
              <div style={{ display: 'flex', gap: '20px', marginBottom: '5px' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ marginBottom: '5px', color: 'black' }}>Referral Template</label>
                  <Select
                    style={{ width: 300 }}
                    onChange={this.handleTemplateChange}
                    value={selectedTemplate}
                  >
                    {resentReplySlug && !this.props.modalType.data.referralTemplate && (
                      <Select.Option value="original_reply">
                        &lt;Original Reply&gt;
                      </Select.Option>
                    )}
                    {visibleTemplates.map(template => (
                      <Select.Option key={template.slug} value={template.slug}>
                        {template.template_name}
                      </Select.Option>
                    ))}
                  </Select>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ marginBottom: '5px', color: 'black' }}>Fax Number</label>
                  <Form.Item
                    rules={[
                      {
                        validator: validatePhoneNumber,
                      },
                    ]}
                    onChange={this.handleFaxNumberChange}
                    style={{ marginBottom: 0 }}
                  >
                    <Input
                      placeholder="Enter Fax number"
                      name="faxnumber"
                      value={this.getTenDigitPhoneNumber(callerName)}
                      style={{ width: 130 }}
                      disabled={isUsingOriginal}
                    />
                    {callerName && !validPhoneNumber(callerName) && <div style={{ color: 'red' }}>Invalid fax number</div>}
                  </Form.Item>
                </div>
              </div>

              {/* Patient Name and DOB Section */}
              <div style={{ display: 'flex', gap: '20px', marginBottom: '5px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <label style={{ marginBottom: '5px', color: 'black' }}>Patient Name</label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Form.Item style={{ marginBottom: 0, flex: 1 }}>
                      <Input
                        value={patientFirstName}
                        placeholder="First name"
                        onChange={this.handleFirstNameChange}
                        disabled={isUsingOriginal}
                      />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0, flex: 1 }}>
                      <Input
                        value={patientMiddleName}
                        placeholder="Middle name"
                        onChange={this.handleMiddleNameChange}
                        disabled={isUsingOriginal}
                      />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0, flex: 1 }}>
                      <Input
                        value={patientLastName}
                        placeholder="Last name"
                        onChange={this.handleLastNameChange}
                        disabled={isUsingOriginal}
                      />
                    </Form.Item>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ marginBottom: '5px', color: 'black' }}>Patient Date of Birth</label>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <DatePicker
                      onChange={this.handleDateChange}
                      name="patientDOB"
                      format={dateFormat}
                      defaultValue={patientDOB && moment(patientDOB, dateFormat)}
                      value={patientDOB && moment(patientDOB, dateFormat)}
                      disabled={isUsingOriginal}
                    />
                  </Form.Item>
                </div>
              </div>

              {/* Referring Provider and Appointment Date Section */}
              <div style={{ display: 'flex', gap: '20px', marginBottom: '5px' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ marginBottom: '5px', color: 'black' }}>Referring Provider</label>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Input
                      value={referringProvider}
                      placeholder="Referring Provider Name"
                      onChange={this.handleProviderChange}
                      style={{ width: 255 }}
                    />
                  </Form.Item>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ marginBottom: '5px', color: 'black' }}>Appointment Date</label>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <DatePicker 
                      showTime
                      onChange={this.handleAppointmentDateChange}
                      name="appointmentDate"
                      format="MM/DD/YYYY hh:mm A"
                      defaultValue={appointmentDate && moment(appointmentDate)}
                      value={appointmentDate && moment(appointmentDate)}
                      style={{ width: 180 }}
                      disabled={isUsingOriginal}
                    />
                  </Form.Item>
                </div>
              </div>
            </div>

            <div style={{ marginLeft: '10px', position: 'relative' }}>
              <Button
                type="primary"
                disabled={isSubmitButtonDisabled() || this.state.isPreviewLoading}
                onClick={this.handleDownload}
                style={{
                  backgroundColor: '#691b9a',
                  borderColor: '#691b9a',
                  color: 'white',
                  opacity: (isSubmitButtonDisabled() || this.state.isPreviewLoading) ? 0.6 : 1,
                }}
              >
                {this.state.isPreviewLoading ? 'Generating Preview...' : 'Preview Referral Reply'}
              </Button>
              {this.state.isPreviewLoading && (
                <Spin style={{ marginLeft: '10px' }} />
              )}
            </div>
          </div>
        </div>
      </ModalWrapper>
    );
  }
}

ReferralReply.defaultProps = {
  data: {},
  error: false,
  errorCode: '',
};

ReferralReply.propTypes = {
  hideModal: PropTypes.func.isRequired,
  modalType: PropTypes.object.isRequired,
  isLoading: PropTypes.bool,
  error: PropTypes.bool,
  errorCode: PropTypes.string,
  data: PropTypes.shape({
    referral_reply: PropTypes.object,
  }),
  getReferralDocumentStatus: PropTypes.func.isRequired,
  sendReferralReply: PropTypes.func.isRequired,
  getReferralReplyPreviewUrl: PropTypes.func.isRequired,
  previewUrl: PropTypes.shape({
    signed_url: PropTypes.string.isRequired,
  }),
};

export default connect(state => ({
  previewUrl: state.records.previewUrl,
  error: state.records.error,
  errorCode: state.records.errorCode,
}), {
  getRecordResponseById,
})(ReferralReply);
